import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, Share, X, Plus } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';
import { usePwaInstall } from '../hooks/usePwaInstall';

const DISMISS_KEY = 'pb_install_dismissed';

export const InstallPrompt = () => {
  const { t } = useLanguage();
  const { canInstall, installed, isIos, promptInstall } = usePwaInstall();
  const { pathname } = useLocation();
  const [dismissed, setDismissed] = useState(!!localStorage.getItem(DISMISS_KEY));
  const [iosReady, setIosReady] = useState(false);

  useEffect(() => {
    // iOS never fires beforeinstallprompt, so the manual steps show up after a short delay
    if (!isIos || installed || dismissed) return;
    const timer = setTimeout(() => setIosReady(true), 3000);
    return () => clearTimeout(timer);
  }, [isIos, installed, dismissed]);

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISS_KEY, '1');
  };

  const install = async () => {
    const accepted = await promptInstall();
    if (accepted) dismiss();
  };

  const showIosSteps = isIos && !canInstall;
  // The dashboard already shows a dedicated install card, no need to stack a banner on top
  if (pathname === '/dashboard') return null;
  if (installed || dismissed || (!canInstall && !(showIosSteps && iosReady))) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-[60] mx-auto max-w-md rounded-2xl border border-[#32CD32]/30 bg-[#0a0a0a]/95 p-4 shadow-2xl backdrop-blur-xl sm:left-auto sm:right-6 animate-in slide-in-from-bottom-4 duration-500"
      data-testid="install-prompt"
    >
      <button
        onClick={dismiss}
        className="absolute right-3 top-3 text-gray-500 transition-colors hover:text-white"
        aria-label={t('installDismiss')}
        data-testid="install-dismiss-btn"
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3 pr-6">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#32CD32]/15">
          <Download className="h-5 w-5 text-[#32CD32]" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white">{t('installTitle')}</p>
          <p className="mt-1 text-xs text-gray-400">{t('installSubtitle')}</p>

          {showIosSteps ? (
            <div className="mt-3 space-y-1.5 text-xs text-gray-300" data-testid="install-ios-steps">
              <p className="flex items-center gap-1.5">
                <span className="text-[#32CD32]">1.</span>
                {t('installIosStep1')}
                <Share className="h-3.5 w-3.5 text-[#32CD32]" />
              </p>
              <p className="flex items-center gap-1.5">
                <span className="text-[#32CD32]">2.</span>
                {t('installIosStep2')}
                <Plus className="h-3.5 w-3.5 text-[#32CD32]" />
              </p>
            </div>
          ) : (
            <Button
              onClick={install}
              className="btn-primary mt-3 h-9 rounded-lg text-xs"
              data-testid="install-app-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              {t('installAction')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
